/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';

import * as angular from 'angular';
import { pad } from 'utils/basic-utils';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import firebase from 'firebase/compat/app';

export class RallyPointService {
	public static $inject = ['$q', 'firebaseProjectMembersStatic', 'firebaseAccessCode', 'firebaseIndices'];
	constructor(
		private $q,
		private firebaseProjectMembersStatic,
		private firebaseAccessCode,
		private firebaseIndices
	) {
		this.logger = LoggerServiceFactory.create('RallyPointService');
	}

	// -----------------------------------------------------
	// internal
	// -----------------------------------------------------
	private logger: ILoggerService;
	private codePath = 'accessCodes/codes/';
	public getBasePm(userId) {
		return { accessLevel: 'General', userId };
	}

	//for easier testing...
	public boilerplate(projectId, planId) {
		if (!projectId) {
			projectId = '-KiaWAvlGwLagS8ABhAc';
		}
		if (!planId) {
			planId = '-KjOXcJLvFkc_MFsp5V1';
		}
		//http://localhost:9001/#/plan/-KiaWAvlGwLagS8ABhAc/-KjOXcJLvFkc_MFsp5V1
		let theCode = '00001';
		let accessCode = {
			projectId: projectId,
			planId: planId,
			timestamp: fbdbTime.syncMoment.add(1, 'days').valueOf(),
		};
		const accessCodePath = fancyFirebaseGetters.getAccessCodesPath(theCode);
		const planPath = fancyFirebaseGetters.getPlanPath(accessCode.projectId, accessCode.planId);
		fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(accessCodePath).set(accessCode);
		fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, accessCode.projectId).update({
			accessCode: theCode,
		});
	}

	public checkForProjectAccess(projectId, userId) {
		return this.firebaseIndices.projectMember(projectId, userId).then(function (pmIndex) {
			return pmIndex.$value !== null;
		});
	}

	public getCodePath(code) {
		//currently if code is undefined, the path will be: 'accessCodes/codes/undefined
		//but if we use fancyFirebaseGetters.getAccessCodesPath(code), this will cause error, because the string will be: 'accessCodes/codes'
		const correctPath = fancyFirebaseGetters.getAccessCodesPath(code);
		//...because it's going to be more stable to just maintain this old bug. Everything that triggers it is in angularjs land. Can fix when rebuilding this
		const legacyCompatPath = code ? correctPath : correctPath + '/undefined';
		return legacyCompatPath;
	}

	public getAccessCode(code) {
		return new this.firebaseAccessCode(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(this.getCodePath(code))
		);
	}

	public createAccessCode(code, projectId, planId) {
		let fbCode = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(this.getCodePath(code));
		let obj = {
			projectId,
			planId,
			timestamp: fbdbTime.syncMoment.add(1, 'hours').valueOf(),
		};

		return this.$q.when(
			fbCode.transaction((existingData) => {
				this.logger.log('transacting', existingData);
				if (this.codeIsAvailable(existingData)) {
					return obj;
				}
			})
		);
	}

	public codeIsAvailable(codeData) {
		return codeData === null || codeData.timestamp < fbdbTime.syncMoment.valueOf();
	}

	//goin' old school with the callback api for the recursion
	//do not call this function directly
	//1) - $value is null or old
	//2) - $value is something
	// Incidentally, this entire thing could probably have been implemented within the structure of a transaction
	// buut... since this is already written, might we well use it. In theory there's slightly less overhead.
	public checkForAccessInternal(cb, count?) {
		if (count > 20) {
			cb(false);
			return;
		}

		let code = this.generateCode();
		//this.logger.log('trying new code...', code);
		let fbCode = this.getAccessCode(code);
		fbCode
			.$loaded()
			.then(() => {
				if (this.codeIsAvailable(fbCode.$value === null ? null : fbCode)) {
					this.logger.log('code is unused or expired');
					cb(fbCode.$ref().ref, code);
				} else {
					this.logger.log('code in use', code, count);
					this.checkForAccessInternal(cb, count ? count + 1 : 1);
				}
				fbCode.$destroy();
			})
			.catch((e) => {
				this.logger.log('the hell did you do? This should not happen!');
				fbCode.$destroy();
			});
	}

	//this is the one you call
	public checkForAccess() {
		return new this.$q((resolve, reject) => {
			this.checkForAccessInternal(function (ref, code) {
				if (ref) {
					resolve({ ref, code });
				} else {
					reject(new Error('Access code retry limit hit, aborting'));
				}
			});
		});
	}

	//this may result in a recursive promise loop. Which will give up after 5 attempts
	public tryToWriteAccessCode(projectId, planId, count?) {
		if (count > 5) {
			return this.$q.rejectErr('Transaction conflicted too many times');
		}
		return this.checkForAccess()
			.then((result) => {
				this.logger.log('code get', result.code);
				return this.createAccessCode(result.code, projectId, planId);
			})
			.then((transactionResult) => {
				if (transactionResult.committed) {
					return transactionResult.snapshot;
				} //all good!
				else {
					return this.tryToWriteAccessCode(projectId, planId, count ? count + 1 : 1);
				} //sketchy af
			});
	}

	//TODO - think of the access code plan index. What is preventing the "multiple codes same plan thing". Reread
	//TODO - also need to multi-client test this + harden it up with transactions
	public tryToCreate(projectId, planId) {
		if (!projectId) {
			projectId = '-KiaWAvlGwLagS8ABhAc';
		}
		if (!planId) {
			planId = '-KjOXcJLvFkc_MFsp5V1';
		}

		let planSnap;

		//check the plan and potentially clear out the old accessCode
		return (
			this.getPlanStuff(projectId, planId)
				.then((snap) => {
					planSnap = snap;
					if (planSnap.child('accessCode').exists()) {
						let p = [];
						let code = planSnap.child('accessCode').val();
						let oldCodeRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
							this.getCodePath(code)
						);
						p.push(oldCodeRef.remove());
						p.push(planSnap.child('rallyMembers').ref.remove());

						return this.$q.all(p);
					} else {
						return;
					}
				})
				//have removed stale pin if it exists, return value isn't interesting
				.then(() => {
					return this.tryToWriteAccessCode(projectId, planId);
				})
				.then((accessCodeSnap) => {
					if (accessCodeSnap && accessCodeSnap.exists()) {
						analyticsEventsService.rallyPointAccessCodeGenerated(accessCodeSnap.key);
						return planSnap.ref.child('accessCode').set(accessCodeSnap.key);
					}
				})
				.then(() => {
					this.logger.log('accessCode finally created');
				})
				.catch((e) => {
					this.logger.log('err', e);
				})
		);
	}

	//tryToCreate();

	//when adding
	//- check /plans for pin data, if it's there, use the pinId to clear out /accessCodes
	//- create a new code
	//- populate access code data on plan

	public getPlanStuff(projectId, planId) {
		const planPath = fancyFirebaseGetters.getPlanPath(projectId, planId);
		let ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, projectId);
		return this.$q.when(ref.once('value'));
	}
	//for(var i = 0; i < 100; i++){ tryToCreate(); }

	public generateCode() {
		//v0: 77777
		//return 77777;

		//v1: random 6 digit number
		let num = Math.floor(Math.random() * 99999);
		return pad(num, 6);

		//v1.5: narrowed keyspace for testing purposes
		// var num = Math.floor(Math.random() * 9);
		// return pad(num, 1)
	}

	public addToRallyMembers(projectId, planId, userId, pmId) {
		if (!projectId && !planId) {
			return this.$q.rejectErr('addToPinMembers - projectId/ planId are requried');
		}

		//do this silly thing to get your pmId or bypass the async stuff if you have it
		return (() => {
			if (pmId) {
				return this.$q.when(pmId);
			} else {
				this.firebaseIndices.projectMember(projectId, userId).then((pmIdx) => {
					this.logger.log('pmIdx', pmIdx);
					if (pmIdx.$value !== null) {
						return pmIdx.$value;
					} else {
						return this.$q.rejectErr('project member index does not exist');
					}
				});
			}
		})().then((pmId) => {
			const pathOfPlan = fancyFirebaseGetters.getPlanPath(projectId, planId);
			let planPath = fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(pathOfPlan, projectId)
				.child('rallyMembers')
				.child(pmId);
			return planPath.set(pmId);
		});
	}
}

angular.module('ticketspaceApp').service('rallyPointService', RallyPointService);
